import { Component, ElementRef, EventEmitter, forwardRef, Input, Optional, Output, ViewChild } from '@angular/core';
import { ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { IonDatetime, IonInput } from '@ionic/angular';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { settings } from 'src/app/settings';

@Component({
  selector: 'mable-time',
  templateUrl: './time.component.html',
  styleUrls: ['./time.component.scss'],
  providers: [
    {
      /*eslint-disable */
      // to fix @typescript-eslint/no-use-before-define
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeComponent),
      multi: true,
      /* eslint-enable */
    },
  ],
})
export class TimeComponent implements ControlValueAccessor {
  @ViewChild(IonDatetime) mabletime: IonDatetime;

  /** Allows for manually setting on the focus on the ion-input */
  @ViewChild(IonInput, { static: true }) ionInputElement: IonInput;

  /** Allows for manually setting on the focus on the timePickerButton */
  @ViewChild('timePickerButton', { read: ElementRef, static: false }) timePickerButton: ElementRef;

  @Input() formControl: UntypedFormControl;

  @Input() formControlName: string;

  @Input() timeDisplayFormat = 'hh:mm a';

  /** The minute options. Default is an interval of 5. */
  @Input() minuteValues = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  /** The datetime modal test id */
  @Input() modalDataTestId = 'mable-time';

  @Output() ionChange = new EventEmitter();

  @Output() ionInput = new EventEmitter();

  /** The id of the error message to describe the input. */
  @Input() ariaDescribedBy = 'errorMessage';

  /** The aria label to attach to the time picker input. */
  @Input() ariaLabel;

  /** The id of the element to label the input. */
  @Input() ariaLabelledBy;

  /** Show button to toggle time picker modal */
  @Input() showToggleButton = false;

  isModalOpen = false;

  // Local form control for time wheel
  localValueControl = new UntypedFormControl();

  // local form control subscription reference for clean-up.
  localTimeChangeSub: Subscription;

  // To add a different Id to each time component.
  instanceId = `time-${timeIds++}`;

  /**
   * Flagger to indicate whether the focus on the input is triggered manually after closing the time picker modal.
   * This is used to prevent an infinite loop of the time picker re-opening.
   */
  restoreFocusOnDismiss = false;

  /**
   * Flagger to indicate if the time picker modal was triggered by the time picker button.
   * This is used to restore the focus on the button once the time picker modal is dismissed.
   */
  triggeredByButton = false;

  /**
   *  get hold of FormControl instance no matter formControl or formControlName is given.
   * If formControlName is given, then this.controlContainer.control is the parent FormGroup (or FormArray) instance.
   */
  get control() {
    const control = this.formControl || this.controlContainer?.control.get(this.formControlName);

    if (control?.value === '') {
      control.setValue(null);
    }

    return control;
  }

  set value(val) {
    // this value is updated by programmatic changes
    if (val !== undefined && this.val !== val) {
      this.val = val;
      this.onChange(val);
      this.onTouch();
    }
  }

  val: any; // this is the updated value that the class accesses

  // ControlContainer made optional for the component to work with ngModel.
  constructor(@Optional() private controlContainer: ControlContainer) {}

  // Function to call when the selected options change.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any) => {
    // console.log(value);
  };

  //   // Function to call when the input is touched (when a star is clicked).
  onTouch = () => {};

  // this method sets the value programmatically
  writeValue(value: any) {
    // Removing timeone offset for the time being to support 12hrs and 24hrs formats from the BE.
    // This line can be removed once we start passing the offset from BE.
    if (value) {
      this.value = moment(value).format(settings.ionicDateTimeFormat);
    } else {
      this.value = value;
    }

    this.ionChange.emit({ detail: { value } });
    this.ionInput.emit({ detail: { value } });
  }

  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  // upon touching the element, this method gets triggered
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  /**
   * Sets modal isOpen state. The isOpen property on ion-modal allows developers to control the presentation state of
   * the modal from their application state.
   *
   * When the modal is dismissed, the focus will be restored on the trigger source, i.e. input if it was triggered by
   * the input, otherwise the time picker button. If the focus is restored to the input, prevent the modal from opening.
   *
   * @param isOpen - This means when isOpen is set to true the modal will be presented and when isOpen is set to false the modal will be dismissed.
   * @param triggeredByButton - Whether the picker is triggered by the time picker button.
   */
  public setOpen(isOpen: boolean, triggeredByButton = false): void {
    // prevent the time picker modal from re-opening if the focus is manually triggered by the dismissal of the time picker
    if (this.restoreFocusOnDismiss) {
      this.restoreFocusOnDismiss = false;
      return;
    }

    if (!isOpen) {
      // unsubscribe from control value changes once the time modal is dimissed.
      this.localTimeChangeSub?.unsubscribe();

      if (this.triggeredByButton) {
        this.timePickerButton?.nativeElement?.lastChild?.focus();
      } else {
        this.restoreFocusOnDismiss = true;
        this.ionInputElement.setFocus();
      }
    } else {
      this.triggeredByButton = triggeredByButton;

      // Setting default value to be assigned to the input on time value confirmation.
      // Workaround for default undefined time value.
      this.localValueControl.setValue(this.control.value || this.getDefaultTime());
    }

    this.isModalOpen = isOpen;
  }

  /**
   * Cancel handler for the time selection modal.
   */
  cancel() {
    this.onTouch();
    this.mabletime.cancel(true);
  }

  /**
   * Confirm handler for time selection modal.
   *
   * @param selectedTime - Current time input value.
   */
  select(selectedTime) {
    this.localTimeChangeSub = this.localValueControl.valueChanges.subscribe((timeValue) => {
      // Workaround for default undefined time value.
      // At the moment the default time value is not set as the input value on modal confirmation.
      // Therefore the parent formcontrol has been decoupled from internal logic. We can directly use form control with default datetime controls once this bug is resovled.
      if (selectedTime && !timeValue) {
        this.writeValue(selectedTime);
      } else {
        // selectedTime can not be used here as the value of control is updated after execution of time modal confirm handler.
        this.writeValue(timeValue);
      }
    });
    // Close modal to update the time input value.
    this.mabletime.confirm(true);
  }

  /**
   * Returns datetime string value according to passed in minuteValues.
   */
  getDefaultTime(): string {
    const now = moment(new Date());
    const hours = now.hour();
    const currentMinutes = now.minutes();
    let defaultMinutes = 0;
    for (const minuteValue of this.minuteValues) {
      if (minuteValue <= currentMinutes) {
        defaultMinutes = minuteValue;
      } else {
        break;
      }
    }

    return moment(`${hours}:${defaultMinutes}`, 'HH:mm').format(settings.ionicDateTimeFormat);
  }
}

// since we're assigning IDs to elements and using them, we need to give each instance of the time a unique id
let timeIds = 0;
